import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { ArrowLeft, CreditCard, Loader2, CheckCircle, AlertCircle, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegistrationPage = () => {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    is_subscribed: false,
    discipline: '',
    experience_level: '',
    special_requests: ''
  });

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/register/${slug}`);
        setForm(response.data);

        // Check for old localStorage pending data and auto-submit it
        const storageKey = `registration_${slug}`;
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          try {
            const data = JSON.parse(stored);
            // Auto-submit any old pending data that wasn't saved to DB yet
            try {
              await axios.post(`${API_URL}/api/register/${slug}`, {
                ...data,
                payment_status: 'pending'
              });
            } catch {
              // Already registered (duplicate email) — that's fine
            }
            localStorage.removeItem(storageKey);
            setSubmitted(true);
          } catch {
            localStorage.removeItem(storageKey);
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Registration not found');
      } finally {
        setLoading(false);
      }
    };
    fetchForm();
  }, [slug]);

  const categories = form?.categories || [];
  const categoryCounts = form?.category_counts || {};

  const isCategoryFull = (catName) => {
    const cat = categories.find(c => c.name.toLowerCase() === catName.toLowerCase());
    if (!cat || !cat.max || cat.max <= 0) return false;
    return (categoryCounts[catName.toLowerCase()] || 0) >= cat.max;
  };

  const getCategorySpots = (catName) => {
    const cat = categories.find(c => c.name.toLowerCase() === catName.toLowerCase());
    if (!cat || !cat.max || cat.max <= 0) return '';
    const count = categoryCounts[catName.toLowerCase()] || 0;
    const remaining = cat.max - count;
    return remaining > 0 ? `${remaining} spot${remaining !== 1 ? 's' : ''} left` : 'FULL';
  };

  const isEventFull = () => {
    if (!form || !form.max_total || form.max_total <= 0) return false;
    return (form.total_count || 0) >= form.max_total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.discipline) {
      toast.error('Please select your discipline');
      return;
    }
    if (!formData.experience_level) {
      toast.error('Please select your experience level');
      return;
    }
    if (isEventFull()) {
      toast.error('This event is full. No spots remaining.');
      return;
    }
    if (isCategoryFull(formData.discipline)) {
      toast.error(`${formData.discipline} spots are full.`);
      return;
    }

    const stripeLink = form?.stripe_link;

    if (stripeLink) {
      // Save registration to DB FIRST, then redirect to payment
      setSubmitting(true);
      try {
        await axios.post(`${API_URL}/api/register/${slug}`, {
          ...formData,
          payment_status: 'pending'
        });
        toast.success('Registration saved! Redirecting to payment...');
        setTimeout(() => { window.location.href = stripeLink; }, 500);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to submit registration');
        setSubmitting(false);
      }
    } else {
      setSubmitting(true);
      try {
        await axios.post(`${API_URL}/api/register/${slug}`, {
          ...formData,
          payment_status: 'pending'
        });
        setSubmitted(true);
        toast.success('Registration submitted!');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to submit registration');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleConfirmAfterPayment = async () => {
    if (!pendingData) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/register/${slug}`, {
        ...pendingData,
        payment_status: 'pending'
      });
      localStorage.removeItem(`registration_${slug}`);
      setPendingData(null);
      setSubmitted(true);
      toast.success('Registration submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit registration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelPending = () => {
    localStorage.removeItem(`registration_${slug}`);
    setPendingData(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Registration Not Available</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Link to="/events">
              <Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />Back to Events</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Registration Received!</h2>
            <p className="text-muted-foreground mb-2">
              Your registration for <span className="font-semibold">{form.event_name}</span> has been received.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              The organizer will verify your payment and confirm your spot.
            </p>
            <Link to="/events">
              <Button variant="outline" className="w-full"><ArrowLeft className="w-4 h-4 mr-2" />Back to Events</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isEventFull()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{form.event_name}</h2>
            <p className="text-muted-foreground mb-4">This event is currently full. Please contact the organizer for waitlist options.</p>
            <Link to="/events">
              <Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />Back to Events</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (pendingData) {
    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-lg mx-auto">
          <Link to="/events" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Events
          </Link>
          <Card>
            <CardHeader className="text-center">
              <CreditCard className="w-12 h-12 text-primary mx-auto mb-2" />
              <CardTitle className="font-display text-xl uppercase tracking-tight">Confirm Your Registration</CardTitle>
              <CardDescription>If you've completed payment on Stripe, confirm below. The organizer will verify your payment.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{pendingData.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{pendingData.email}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Discipline</span><span className="font-medium capitalize">{pendingData.discipline}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Level</span><span className="font-medium capitalize">{pendingData.experience_level}</span></div>
              </div>
              <Button className="w-full" size="lg" onClick={handleConfirmAfterPayment} disabled={submitting} data-testid="confirm-registration-btn">
                {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</> : <><CheckCircle className="w-5 h-5 mr-2" /> I've Paid — Submit Registration</>}
              </Button>
              <p className="text-xs text-muted-foreground text-center">Your spot will be confirmed once the organizer verifies payment.</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => { window.location.href = form.stripe_link; }} data-testid="retry-payment-btn">
                  <CreditCard className="w-4 h-4 mr-2" />Go Back to Payment
                </Button>
                <Button variant="ghost" className="flex-1" onClick={handleCancelPending} data-testid="cancel-pending-btn">Start Over</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/events" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Events
        </Link>

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="font-display text-2xl md:text-3xl uppercase tracking-tight">{form.event_name}</CardTitle>
            {form.description && <CardDescription className="text-base mt-2">{form.description}</CardDescription>}
            {form.price > 0 && <p className="text-lg font-semibold text-primary mt-2">${form.price} CAD</p>}
          </CardHeader>
          <CardContent>
            {/* Capacity info */}
            {(categories.length > 0 || form.max_total > 0) && (
              <div className="mb-6 bg-muted/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Spots Available
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {categories.filter(c => c.max > 0).map(cat => {
                    const count = categoryCounts[cat.name.toLowerCase()] || 0;
                    const full = count >= cat.max;
                    return (
                      <div key={cat.name} className={`rounded p-2 ${full ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                        <span className="font-medium">{cat.name}:</span> {count}/{cat.max}
                        {full && <span className="ml-1 font-bold">FULL</span>}
                      </div>
                    );
                  })}
                </div>
                {form.max_total > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">Total: {form.total_count || 0}/{form.max_total} spots filled</p>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Personal Information</h3>
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Your full name" required data-testid="reg-name" />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="your@email.com" required data-testid="reg-email" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(403) 555-0123" required data-testid="reg-phone" />
                </div>
                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="City, Province" required data-testid="reg-address" />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Roping Details</h3>
                <div>
                  <Label>Discipline *</Label>
                  <Select value={formData.discipline} onValueChange={(v) => setFormData({ ...formData, discipline: v })}>
                    <SelectTrigger data-testid="reg-discipline">
                      <SelectValue placeholder="Select your discipline" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.length > 0 ? (
                        categories.map(cat => {
                          const full = isCategoryFull(cat.name);
                          const spots = getCategorySpots(cat.name);
                          return (
                            <SelectItem key={cat.name} value={cat.name.toLowerCase()} disabled={full}>
                              {cat.name} {spots && `(${spots})`}
                            </SelectItem>
                          );
                        })
                      ) : (
                        <>
                          <SelectItem value="header">Header</SelectItem>
                          <SelectItem value="heeler">Heeler</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Experience Level *</Label>
                  <Select value={formData.experience_level} onValueChange={(v) => setFormData({ ...formData, experience_level: v })}>
                    <SelectTrigger data-testid="reg-experience">
                      <SelectValue placeholder="Select your experience level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="special_requests">Special Requests or Notes</Label>
                  <Textarea id="special_requests" value={formData.special_requests} onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })} placeholder="Any special requests, dietary restrictions, or notes for the organizers..." rows={3} data-testid="reg-notes" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Have you subscribed to our website yet?</Label>
                <RadioGroup value={formData.is_subscribed ? "yes" : "no"} onValueChange={(v) => setFormData({ ...formData, is_subscribed: v === "yes" })} className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="subscribed-yes" />
                    <Label htmlFor="subscribed-yes" className="font-normal cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="subscribed-no" />
                    <Label htmlFor="subscribed-no" className="font-normal cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
                <p className="text-sm text-muted-foreground">To access your photos and videos of the event, you need to be a current subscriber.</p>
              </div>

              <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="register-submit-btn">
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                ) : form?.stripe_link ? (
                  <><CreditCard className="w-5 h-5 mr-2" /> Register & Pay {form.price > 0 && `($${form.price})`}</>
                ) : (
                  <>Register</>
                )}
              </Button>

              {form?.stripe_link && (
                <p className="text-xs text-muted-foreground text-center">
                  You'll be redirected to Stripe to complete payment. After paying, return here to confirm your registration.
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegistrationPage;
